/**
 * ComparisonSummary
 *
 * Summary tab showing:
 *   - Side-by-side stats table (always visible)
 *   - Aid station time decomposition (race-linked only)
 *   - Checkpoint timeline table (race-linked only)
 *   - Section performance bars (race-linked only)
 *   - Cumulative gap step-line chart at checkpoints (race-linked only)
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CheckpointComparisonTable from './CheckpointComparisonTable';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Divider,
  useTheme,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartTooltip,
  ReferenceLine,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { tokens } from '../../../theme';

const COLOR_A = '#3b82f6';
const COLOR_B = '#f59e0b';

function formatDuration(seconds) {
  if (seconds == null) return '—';
  const abs = Math.abs(seconds);
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = Math.floor(abs % 60);
  const sign = seconds < 0 ? '−' : '';
  if (h > 0) return `${sign}${h}h ${m.toString().padStart(2, '0')}m`;
  return `${sign}${m}m ${s.toString().padStart(2, '0')}s`;
}

function formatPace(v) {
  if (v == null) return '—';
  const m = Math.floor(v);
  const s = Math.round((v - m) * 60);
  return `${m}:${s.toString().padStart(2, '0')} /km`;
}

function formatKm(m) {
  if (m == null) return '—';
  return `${(m / 1000).toFixed(1)} km`;
}

// ---------------------------------------------------------------------------
// Side-by-side stats table
// ---------------------------------------------------------------------------

const STAT_ROWS = [
  { i18nKey: 'totalDistanceStat', defaultLabel: 'Total Distance', keyA: 'total_distance', keyB: 'total_distance', format: formatKm, lowerBetter: false },
  { i18nKey: 'finishTimeStat', defaultLabel: 'Finish Time', keyA: 'total_duration', keyB: 'total_duration', format: formatDuration, lowerBetter: true },
  { i18nKey: 'movingTimeStat', defaultLabel: 'Moving Time', keyA: 'total_moving_time', keyB: 'total_moving_time', format: formatDuration, lowerBetter: true },
  { i18nKey: 'avgPace', defaultLabel: 'Avg Pace', keyA: 'avg_pace', keyB: 'avg_pace', format: formatPace, lowerBetter: true },
  { i18nKey: 'avgNormPace', defaultLabel: 'Avg Norm. Pace', keyA: 'avg_gap', keyB: 'avg_gap', format: formatPace, lowerBetter: true },
  { i18nKey: 'avgHR', defaultLabel: 'Avg HR', keyA: 'avg_heart_rate', keyB: 'avg_heart_rate', format: (v) => v != null ? `${Math.round(v)} bpm` : '—', lowerBetter: null },
  { i18nKey: 'elevGainStat', defaultLabel: 'Elev Gain', keyA: 'total_elevation_gain', keyB: 'total_elevation_gain', format: (v) => v != null ? `${Math.round(v)} m` : '—', lowerBetter: false },
  { i18nKey: 'staminaScore', defaultLabel: 'Stamina Score', keyA: 'stamina_score', keyB: 'stamina_score', format: (v) => v?.toFixed(1) ?? '—', lowerBetter: null },
];

function StatRow({ row, analysisA, analysisB }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  const valA = analysisA?.[row.keyA];
  const valB = analysisB?.[row.keyB];

  let aColor, bColor;
  if (row.lowerBetter !== null && valA != null && valB != null) {
    const aBetter = row.lowerBetter ? valA < valB : valA > valB;
    const bBetter = row.lowerBetter ? valB < valA : valB > valA;
    aColor = aBetter ? '#22c55e' : bBetter ? '#ef4444' : undefined;
    bColor = bBetter ? '#22c55e' : aBetter ? '#ef4444' : undefined;
  }

  return (
    <TableRow sx={{ '&:nth-of-type(even)': { backgroundColor: colors.grey[800] } }}>
      <TableCell sx={{ color: colors.grey[400], fontSize: '0.8rem' }}>{t(row.i18nKey, row.defaultLabel)}</TableCell>
      <TableCell align="right" sx={{ color: aColor || colors.grey[100], fontWeight: aColor ? 'bold' : 'normal', fontSize: '0.85rem' }}>
        {row.format(valA)}
      </TableCell>
      <TableCell align="right" sx={{ color: bColor || colors.grey[100], fontWeight: bColor ? 'bold' : 'normal', fontSize: '0.85rem' }}>
        {row.format(valB)}
      </TableCell>
    </TableRow>
  );
}

function StatsTable({ analysisA, analysisB }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ backgroundColor: colors.grey[900], color: colors.grey[400], fontWeight: 'bold', width: 130 }}>{t('metricColumn', 'Metric')}</TableCell>
            <TableCell align="right" sx={{ backgroundColor: colors.grey[900], color: COLOR_A, fontWeight: 'bold' }}>A</TableCell>
            <TableCell align="right" sx={{ backgroundColor: colors.grey[900], color: COLOR_B, fontWeight: 'bold' }}>B</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {STAT_ROWS.map((row) => (
            <StatRow key={row.label} row={row} analysisA={analysisA} analysisB={analysisB} />
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Aid station summary card
// ---------------------------------------------------------------------------

function AidStationCard({ checkpoints, analysisA, analysisB }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  const totalStopA = checkpoints?.reduce((s, cp) => s + (cp.stop_a_s || 0), 0) || 0;
  const totalStopB = checkpoints?.reduce((s, cp) => s + (cp.stop_b_s || 0), 0) || 0;

  const totalDelta = (analysisA?.total_duration ?? 0) - (analysisB?.total_duration ?? 0);
  const movingDelta = (analysisA?.total_moving_time ?? 0) - (analysisB?.total_moving_time ?? 0);
  const stopDelta = totalStopA - totalStopB;

  return (
    <Box
      sx={{ backgroundColor: colors.grey[800], borderRadius: 2, p: 2, mb: 2 }}
    >
      <Typography variant="subtitle1" fontWeight="bold" color={colors.grey[100]} mb={1}>
        {t('aidStationTimeSummary', 'Aid Station Time Summary')}
      </Typography>
      <Box display="flex" gap={4} flexWrap="wrap">
        <Box>
          <Typography variant="caption" color={colors.grey[400]}>{t('totalStopsA', 'Total stops — A')}</Typography>
          <Typography variant="h6" color={COLOR_A} fontWeight="bold">{formatDuration(totalStopA)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color={colors.grey[400]}>{t('totalStopsB', 'Total stops — B')}</Typography>
          <Typography variant="h6" color={COLOR_B} fontWeight="bold">{formatDuration(totalStopB)}</Typography>
        </Box>
      </Box>
      {Math.abs(totalDelta) > 10 && (
        <Box mt={1.5} sx={{ backgroundColor: colors.grey[700], borderRadius: 1, p: 1 }}>
          <Typography variant="caption" color={colors.grey[300]} display="block">
            {t('ofTotalGap', 'Of the {{duration}} total gap:', { duration: formatDuration(Math.abs(totalDelta)) })}
          </Typography>
          <Typography variant="caption" color={colors.grey[200]} display="block">
            {t('movingTimeDifference', '• Moving time difference:')} <b>{formatDuration(movingDelta)}</b>
          </Typography>
          <Typography variant="caption" color={colors.grey[200]} display="block">
            {t('aidStationDifference', '• Aid station difference:')} <b>{formatDuration(stopDelta)}</b>
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// CheckpointTable is now in CheckpointComparisonTable.jsx (imported above)

// ---------------------------------------------------------------------------
// Cumulative gap progression chart at checkpoints
// ---------------------------------------------------------------------------

function GapProgressionChart({ checkpoints }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  const data = useMemo(() => {
    if (!checkpoints?.length) return [];
    return checkpoints.map((cp, i) => ({
      name: cp.name || `CP ${i + 1}`,
      gap: cp.gap_at_cp_s ?? 0,
    }));
  }, [checkpoints]);

  if (!data.length) return null;

  return (
    <Box mb={3}>
      <Typography variant="subtitle1" fontWeight="bold" color={colors.grey[100]} mb={1}>
        {t('cumulativeGapAtCheckpoints', 'Cumulative Gap at Checkpoints')}
      </Typography>
      <Typography variant="caption" color={colors.grey[400]} display="block" mb={1}>
        {t('bArrivedLaterThanA', 'Positive = B arrived later than A')}
      </Typography>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data} margin={{ top: 4, right: 20, left: 20, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
          <XAxis dataKey="name" tick={{ fill: colors.grey[400], fontSize: 10 }} />
          <YAxis tick={{ fill: colors.grey[400], fontSize: 10 }} tickFormatter={formatDuration} width={52} />
          <ReferenceLine y={0} stroke={colors.grey[300]} strokeWidth={2} />
          <RechartTooltip
            formatter={(v) => [formatDuration(v), t('gapBA', 'Gap (B − A)')]}
            contentStyle={{ backgroundColor: colors.grey[800], border: `1px solid ${colors.grey[700]}` }}
          />
          <Line
            dataKey="gap"
            stroke={colors.grey[200]}
            strokeWidth={2}
            dot={{ fill: colors.blueAccent[400], r: 5 }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ComparisonSummary({ analysisA, analysisB, checkpoints, swapped }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  const hasCheckpoints = checkpoints && checkpoints.length > 0;

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={2}>
        {t('summary', 'Summary')}
      </Typography>

      <StatsTable analysisA={analysisA} analysisB={analysisB} />

      {hasCheckpoints && (
        <>
          <Divider sx={{ my: 3, borderColor: colors.grey[700] }} />
          <AidStationCard checkpoints={checkpoints} analysisA={analysisA} analysisB={analysisB} />
          <GapProgressionChart checkpoints={checkpoints} />
          <Typography variant="subtitle1" fontWeight="bold" color="grey.100" mb={1}>
            {t('checkpointTimeline', 'Checkpoint Timeline')}
          </Typography>
          <CheckpointComparisonTable checkpoints={checkpoints} race={analysisA?.race} />
        </>
      )}
    </Box>
  );
}
